import { Link, useLocation } from "react-router-dom";
import {
  BLOG_CATEGORIES,
  formatPostDateline,
  getCategoryBySlug,
  type BlogPost,
} from "@/lib/blog";
import { BLOG, BLOG_CATEGORY, BLOG_POST } from "@/routes";

/**
 * Non-interactive category label for use inside card links (an <a> cannot nest
 * another <a>). For a clickable chip, use CategoryChipLink.
 */
export const CategoryChip = ({ categorySlug }: { categorySlug: string }) => {
  const category = getCategoryBySlug(categorySlug);
  if (!category) return null;

  return (
    <span className="inline-flex w-fit rounded-full bg-brand-500/10 px-2.5 py-0.5 text-ui-micro font-semibold uppercase tracking-wide text-brand-600">
      {category.name}
    </span>
  );
};

export const CategoryChipLink = ({ categorySlug }: { categorySlug: string }) => {
  const category = getCategoryBySlug(categorySlug);
  if (!category) return null;

  return (
    <Link
      to={BLOG_CATEGORY({ categorySlug })}
      className="inline-flex w-fit rounded-full bg-brand-500/10 px-2.5 py-0.5 text-ui-micro font-semibold uppercase tracking-wide text-brand-600 transition-colors hover:bg-brand-500/20"
    >
      {category.name}
    </Link>
  );
};

/**
 * Category pill navigation shared by the blog index and category pages.
 * Real links (crawlable), horizontally scrollable on small screens.
 */
export const CategoryPills = () => {
  const { pathname } = useLocation();
  const pillClass = (active: boolean) =>
    `shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
      active
        ? "border-foreground bg-foreground text-background"
        : "border-foreground/15 text-foreground/70 hover:border-brand-500/50 hover:text-brand-600"
    }`;

  return (
    <nav aria-label="Blog categories" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
      <Link to={BLOG()} className={pillClass(pathname === BLOG())}>
        All posts
      </Link>
      {BLOG_CATEGORIES.map(category => (
        <Link
          key={category.slug}
          to={BLOG_CATEGORY({ categorySlug: category.slug })}
          className={pillClass(pathname === BLOG_CATEGORY({ categorySlug: category.slug }))}
        >
          {category.name}
        </Link>
      ))}
    </nav>
  );
};

const CardArt = ({ post, sizesHint }: { post: BlogPost; sizesHint?: string }) => {
  if (!post.cardImage) return null;

  return (
    <div className="overflow-hidden rounded-lg bg-muted">
      <img
        src={post.cardImage}
        alt=""
        loading="lazy"
        sizes={sizesHint}
        className="aspect-[1200/630] w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
      />
    </div>
  );
};

/** Standard image card for index sections, category pages, and related posts. */
export const BlogPostCard = ({ post }: { post: BlogPost }) => (
  <article>
    <Link to={BLOG_POST({ slug: post.slug })} className="group flex flex-col gap-3">
      <CardArt post={post} sizesHint="(max-width: 640px) 100vw, 320px" />
      <div className="flex flex-col gap-1.5">
        <CategoryChip categorySlug={post.category} />
        <h3 className="m-0 text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-brand-600 line-clamp-2">
          {post.title}
        </h3>
        <p className="m-0 text-sm leading-relaxed text-foreground/65 line-clamp-2">{post.description}</p>
        <p className="m-0 text-xs text-foreground/50">
          {formatPostDateline(post)} · {post.readingMinutes} min read
        </p>
      </div>
    </Link>
  </article>
);

/** Featured treatment for the newest/pinned post at the top of the index. */
export const BlogFeaturedCard = ({ post }: { post: BlogPost }) => (
  <article>
    <Link
      to={BLOG_POST({ slug: post.slug })}
      className="group grid items-center gap-5 rounded-xl border border-foreground/10 p-4 transition-colors hover:border-brand-500/40 sm:p-5 md:grid-cols-2 md:gap-8"
    >
      <CardArt post={post} sizesHint="(max-width: 768px) 100vw, 480px" />
      <div className="flex flex-col gap-2.5">
        <CategoryChip categorySlug={post.category} />
        <h2 className="m-0 text-2xl font-semibold leading-tight tracking-tight text-foreground transition-colors group-hover:text-brand-600 md:text-3xl">
          {post.title}
        </h2>
        <p className="m-0 leading-relaxed text-foreground/70 line-clamp-3">{post.description}</p>
        <p className="m-0 text-sm text-foreground/50">
          {post.author} · {formatPostDateline(post)} · {post.readingMinutes} min read
        </p>
      </div>
    </Link>
  </article>
);

/** Compact text row for the Guides shelf — quieter than the topical sections. */
export const BlogPostRow = ({ post }: { post: BlogPost }) => (
  <article className="py-3.5 first:pt-0 last:pb-0">
    <Link to={BLOG_POST({ slug: post.slug })} className="group flex flex-col gap-0.5">
      <h3 className="m-0 text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-brand-600">
        {post.title}
      </h3>
      <p className="m-0 text-sm text-foreground/60 line-clamp-1">{post.description}</p>
    </Link>
  </article>
);
